import {rectSortingStrategy, SortableContext} from '@dnd-kit/sortable';
import React, {memo, useEffect, useMemo, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {DEFAULT_PANEL_SIZE} from '../../util/panelbankTypes';
import {LayedOutPanelWithRef} from '../../util/panelTypes';
import {ResizableWorkspacePanel} from '../Panel/WorkspacePanel/ResizableWorkspacePanel';
import {WorkspacePanelContainer} from '../Panel/WorkspacePanel/WorkspacePanel2';
import {WorkspacePanelResizePreview} from '../Panel/WorkspacePanel/WorkspacePanelResizePreview';
import {EmptySectionMessageNoPanels} from './EmptyMessages';
import {ResizableWorkspaceSection} from './ResizableWorkspaceSection';
import {getPanelsPerPage, getSectionHeight} from './resizeUtils';
import {getDndSortablePanelId, WORKSPACE_MARGIN_X_TW} from './utils';
import {useWorkspaceSectionLayoutConfigs} from './WorkspaceSectionLayoutConfigs';

const WorkspaceFlowLayoutComp = ({
  filteredPanels,
  isProjectReadOnly,
  panelBankSectionConfigRef,
}: {
  filteredPanels: LayedOutPanelWithRef[];
  isProjectReadOnly: boolean;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
}) => {
  const {currentPage, initialRowsPerPage, panelHeight, columnsPerPage} =
    useWorkspaceSectionLayoutConfigs();

  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  useEffect(() => {
    setRowsPerPage(initialRowsPerPage);
  }, [initialRowsPerPage]);

  const [resizingSectionHeight, setResizingSectionHeight] = useState<
    number | null
  >(null);

  const [panelResizePreview, setPanelResizePreview] =
    useState<WorkspacePanelResizePreview | null>(null);

  // This captures both section resizing and panel resizing
  const isResizing =
    resizingSectionHeight != null || panelResizePreview != null;

  // This needs to be memoized because we update rowsPerPage
  // when section is resizing.
  const panelsPerPage = useMemo(
    () => getPanelsPerPage({columnsPerPage, rowsPerPage}),
    [columnsPerPage, rowsPerPage]
  );

  const filteredPanelsOnActivePage = useMemo(() => {
    const startIndex = currentPage * panelsPerPage;
    const endIndex = startIndex + panelsPerPage;

    // Do not loop through every panel to determine if it should be
    // rendered in the section. Just take advantage of array slicing.
    return filteredPanels.slice(startIndex, endIndex);
  }, [filteredPanels, currentPage, panelsPerPage]);

  const sectionHeight = useMemo(
    () =>
      getSectionHeight({
        rowsPerPage,
        panelHeight,
        columnsPerPage,
        numPanelsOnPage: filteredPanelsOnActivePage.length,
      }),
    [
      columnsPerPage,
      panelHeight,
      rowsPerPage,
      filteredPanelsOnActivePage.length,
    ]
  );

  const items = useMemo(() => {
    return filteredPanelsOnActivePage.map(panel =>
      getDndSortablePanelId(panel.ref)
    );
  }, [filteredPanelsOnActivePage]);

  if (filteredPanels.length === 0) {
    return (
      <div
        className="flex h-full w-full items-center justify-center"
        style={{height: DEFAULT_PANEL_SIZE}}>
        <EmptySectionMessageNoPanels
          panelBankSectionConfigRef={panelBankSectionConfigRef}
        />
      </div>
    );
  }

  return (
    // position relative needs to be set on the parent element to allow for
    // panel resize preview to be positioned absolutely
    <div className="relative">
      <ResizableWorkspaceSection
        className={twMerge(
          WORKSPACE_MARGIN_X_TW,
          // When user is dragging the resize handle for sections,
          // we disable text from being selected and show the correct cursor.
          isResizing && 'pointer-events-none !cursor-row-resize select-none'
        )}
        height={resizingSectionHeight ?? sectionHeight}
        isResizing={isResizing}
        panelsPerPage={panelsPerPage}
        setResizingSectionHeight={setResizingSectionHeight}
        setRowsPerPage={setRowsPerPage}>
        <SortableContext items={items} strategy={rectSortingStrategy}>
          {filteredPanelsOnActivePage.map((panel, index) => (
            <ResizableWorkspacePanel
              key={getDndSortablePanelId(panel.ref)}
              index={index}
              numVisiblePanels={filteredPanelsOnActivePage.length}
              panelResizePreview={panelResizePreview}
              setPanelResizePreview={setPanelResizePreview}>
              <WorkspacePanelContainer
                isProjectReadOnly={isProjectReadOnly}
                panelBankSectionConfigRef={panelBankSectionConfigRef}
                panelRef={panel.ref}
              />
            </ResizableWorkspacePanel>
          ))}
        </SortableContext>
      </ResizableWorkspaceSection>
      {/*
        Ensure we are NOT conditionally rendering the panel resize preview AND
        we are using transforms to position the preview.
        This is important to maintain performant resize interactions.
       */}
      <WorkspacePanelResizePreview panelResizePreview={panelResizePreview} />
    </div>
  );
};

export const WorkspaceFlowLayout = memo(WorkspaceFlowLayoutComp);
